(function () {
  'use strict';

  if (window.FLCFlipbookModal) return;

  const PDF_JS_URL = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
  const PDF_WORKER_URL = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
  const PAGE_FLIP_URL = 'https://cdn.jsdelivr.net/npm/page-flip@2.0.7/dist/js/page-flip.browser.min.js';
  const MAX_RENDER_WIDTH = 5200;
  const MIN_ZOOM = 1;
  const MAX_ZOOM = 2.5;
  const ZOOM_STEP = 0.25;

  const modal = document.querySelector('[data-flipbook-modal]');
  if (!modal) return;

  const dialog = modal.querySelector('.flipbook-modal__dialog');
  const title = modal.querySelector('[data-flipbook-title]');
  const status = modal.querySelector('[data-flipbook-status]');
  const viewport = modal.querySelector('[data-flipbook-viewport]');
  const stage = modal.querySelector('[data-flipbook-stage]');
  const book = modal.querySelector('[data-flipbook-book]');
  const prevButtons = [...modal.querySelectorAll('[data-flipbook-prev]')];
  const nextButtons = [...modal.querySelectorAll('[data-flipbook-next]')];
  const pageCount = modal.querySelector('[data-flipbook-page-count]');
  const zoomOutButton = modal.querySelector('[data-flipbook-zoom-out]');
  const zoomInButton = modal.querySelector('[data-flipbook-zoom-in]');
  const zoomLevelText = modal.querySelector('[data-flipbook-zoom-level]');
  const downloadLink = modal.querySelector('[data-flipbook-download]');

  let pdfDocument = null;
  let pdfUrl = '';
  let pageFlip = null;
  let currentPageIndex = 0;
  let logicalPageCount = 0;
  let zoomLevel = 1;
  let renderKey = 0;
  let previousActiveElement = null;
  let pdfJsPromise = null;
  let pageFlipPromise = null;
  let resizeTimer = null;
  let splitSpreads = true;
  let coverAsSingle = false;
  let activePageSize = null;
  let firstBookPdfPage = 1;
  let lastBookPdfPage = 1;
  let renderedPdfPages = new Set();
  let pdfPageGroups = new Map();
  let fitScale = 1;
  let stageBaseWidth = 0;
  let stageBaseHeight = 0;
  let panX = 0;
  let panY = 0;
  let dragStartX = 0;
  let dragStartY = 0;
  let dragOriginX = 0;
  let dragOriginY = 0;
  let isDragging = false;

  function getActiveBook() {
    return stage.querySelector('[data-flipbook-book]');
  }

  function getActiveCover() {
    return stage.querySelector('[data-flipbook-cover]');
  }

  function loadScript(src, globalTest) {
    return new Promise((resolve, reject) => {
      if (globalTest()) {
        resolve();
        return;
      }

      const existing = document.querySelector(`script[src="${src}"]`);
      if (existing) {
        existing.addEventListener('load', resolve, { once: true });
        existing.addEventListener('error', reject, { once: true });
        return;
      }

      const script = document.createElement('script');
      script.src = src;
      script.async = true;
      script.onload = resolve;
      script.onerror = reject;
      document.head.appendChild(script);
    });
  }

  async function loadPdfJs() {
    if (!pdfJsPromise) {
      pdfJsPromise = loadScript(PDF_JS_URL, () => !!window.pdfjsLib).then(() => {
        window.pdfjsLib.GlobalWorkerOptions.workerSrc = PDF_WORKER_URL;
        return window.pdfjsLib;
      });
    }

    return pdfJsPromise;
  }

  async function loadPageFlip() {
    if (!pageFlipPromise) {
      pageFlipPromise = loadScript(PAGE_FLIP_URL, () => !!window.St?.PageFlip).then(() => window.St.PageFlip);
    }

    return pageFlipPromise;
  }

  function setStatus(message) {
    status.textContent = message || '';
  }

  function resetBook() {
    const activeBook = getActiveBook();

    if (pageFlip) {
      pageFlip.destroy();
      pageFlip = null;
    }

    if (activeBook) {
      activeBook.innerHTML = '';
      activeBook.className = 'flipbook-modal__book';
      activeBook.style.width = '';
      activeBook.style.height = '';
    }

    stage.querySelectorAll('[data-flipbook-cover]').forEach((cover) => cover.remove());
    stage.style.width = '';
    stage.style.height = '';
    coverAsSingle = false;
    activePageSize = null;
    firstBookPdfPage = 1;
    lastBookPdfPage = 1;
    renderedPdfPages = new Set();
    pdfPageGroups = new Map();
    fitScale = 1;
    stageBaseWidth = 0;
    stageBaseHeight = 0;
    panX = 0;
    panY = 0;
    applyPan();
  }

  function setControlsDisabled(disabled) {
    prevButtons.forEach((button) => {
      button.disabled = disabled || currentPageIndex <= 0;
    });
    nextButtons.forEach((button) => {
      button.disabled = disabled || !logicalPageCount || currentPageIndex >= logicalPageCount - 1;
    });
    zoomOutButton.disabled = disabled || zoomLevel <= MIN_ZOOM;
    zoomInButton.disabled = disabled || zoomLevel >= MAX_ZOOM;
  }

  function getVisiblePageText() {
    if (!logicalPageCount) return '';

    const isPortrait = window.matchMedia('(max-width: 749px)').matches;
    const firstPage = currentPageIndex + 1;
    if (coverAsSingle && (currentPageIndex === 0 || currentPageIndex === logicalPageCount - 1)) return `Page ${firstPage} of ${logicalPageCount}`;

    const secondPage = !isPortrait && firstPage < logicalPageCount ? firstPage + 1 : null;

    return secondPage
      ? `Page ${firstPage}-${secondPage} of ${logicalPageCount}`
      : `Page ${firstPage} of ${logicalPageCount}`;
  }

  function updateControls(disabled = false) {
    pageCount.textContent = getVisiblePageText();
    zoomLevelText.textContent = `${Math.round(zoomLevel * 100)}%`;
    setControlsDisabled(disabled);
  }

  async function getSpreadLayout() {
    const pagesToInspect = Math.min(pdfDocument.numPages, 6);
    const pageViewports = [];
    let representativeViewport = null;
    let widestRatio = 0;

    for (let pageNumber = 1; pageNumber <= pagesToInspect; pageNumber += 1) {
      const page = await pdfDocument.getPage(pageNumber);
      const viewport = page.getViewport({ scale: 1 });
      const ratio = viewport.width / viewport.height;
      pageViewports.push({ pageNumber, ratio, viewport });

      if (ratio > widestRatio) {
        widestRatio = ratio;
        representativeViewport = viewport;
      }
    }

    const shouldSplit = widestRatio > 1.15;
    if (shouldSplit) {
      representativeViewport = pageViewports.find((item) => item.pageNumber > 1 && item.ratio > 1.15)?.viewport
        || pageViewports.find((item) => item.ratio > 1.15)?.viewport
        || representativeViewport;
    }

    return {
      split: shouldSplit,
      viewport: representativeViewport
    };
  }

  function getPageSize(referenceViewport) {
    const rect = viewport.getBoundingClientRect();
    const availableWidth = Math.max(rect.width - 8, 320);
    const availableHeight = Math.max(rect.height - 8, 220);
    const rawPageRatio = splitSpreads ? (referenceViewport.width / 2) / referenceViewport.height : referenceViewport.width / referenceViewport.height;
    const isPortrait = window.matchMedia('(max-width: 749px)').matches;
    const visiblePages = isPortrait ? 1 : 2;

    if (isPortrait) {
      const pageWidth = availableWidth;
      const pageHeight = pageWidth / rawPageRatio;

      return {
        height: Math.floor(pageHeight),
        width: Math.floor(pageWidth)
      };
    }

    let pageHeight = availableHeight;
    let pageWidth = pageHeight * rawPageRatio;

    if (pageWidth * visiblePages > availableWidth) {
      pageWidth = availableWidth / visiblePages;
      pageHeight = pageWidth / rawPageRatio;
    }

    return {
      height: Math.floor(pageHeight),
      width: Math.floor(pageWidth)
    };
  }

  function getVisualScale() {
    return fitScale * zoomLevel;
  }

  function updateFitScale() {
    if (!stageBaseWidth || !stageBaseHeight) {
      fitScale = 1;
      return;
    }

    const isPortrait = window.matchMedia('(max-width: 749px)').matches;
    const widthScale = Math.max(0.2, (viewport.clientWidth - 4) / stageBaseWidth);
    const heightScale = Math.max(0.2, (viewport.clientHeight - 4) / stageBaseHeight);

    fitScale = isPortrait ? widthScale : Math.min(widthScale, heightScale);
  }

  function getPanBounds() {
    const visualScale = getVisualScale();
    const baseWidth = stageBaseWidth || stage.offsetWidth;
    const baseHeight = stageBaseHeight || stage.offsetHeight;
    const maxX = Math.max(0, ((baseWidth * visualScale) - viewport.clientWidth) / 2);
    const maxY = Math.max(0, ((baseHeight * visualScale) - viewport.clientHeight) / 2);

    return { maxX, maxY };
  }

  function clampPan() {
    const { maxX, maxY } = getPanBounds();

    panX = Math.min(maxX, Math.max(-maxX, panX));
    panY = Math.min(maxY, Math.max(-maxY, panY));
    viewport.classList.toggle('is-pannable', maxX > 0 || maxY > 0);
  }

  function applyPan() {
    clampPan();
    stage.style.transform = `translate3d(${panX}px, ${panY}px, 0) scale(${getVisualScale()})`;
  }

  function centerStage() {
    updateFitScale();
    panX = 0;
    panY = 0;
    requestAnimationFrame(applyPan);
  }

  function cropCanvas(sourceCanvas, side) {
    const cropWidth = splitSpreads ? Math.floor(sourceCanvas.width / 2) : sourceCanvas.width;
    const cropHeight = sourceCanvas.height;
    const sourceX = splitSpreads && side === 'right' ? cropWidth : 0;
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d', { alpha: false });

    canvas.width = cropWidth;
    canvas.height = cropHeight;
    context.drawImage(sourceCanvas, sourceX, 0, cropWidth, cropHeight, 0, 0, cropWidth, cropHeight);

    return canvas.toDataURL('image/png');
  }

  function createFlipPage(imageUrl, label) {
    const page = document.createElement('div');
    const image = document.createElement('img');

    page.className = 'flipbook-modal__page';
    if (imageUrl) {
      image.src = imageUrl;
    } else {
      image.hidden = true;
      page.classList.add('is-loading');
    }
    image.alt = label;
    image.className = 'flipbook-modal__page-image';
    image.draggable = false;
    page.appendChild(image);

    return page;
  }

  function setFlipPageImage(page, imageUrl, label) {
    const image = page?.querySelector('.flipbook-modal__page-image');
    if (!image) return;

    image.src = imageUrl;
    image.alt = label;
    image.hidden = false;
    page.classList.remove('is-loading');
  }

  function createCoverPage(imageUrl, label, pageSize, position) {
    const cover = document.createElement('div');
    const image = document.createElement('img');
    const isMobilePortrait = window.matchMedia('(max-width: 749px)').matches;

    cover.className = 'flipbook-modal__cover';
    cover.setAttribute('data-flipbook-cover', position);
    cover.style.width = `${isMobilePortrait ? pageSize.width : pageSize.width * 2}px`;
    cover.style.height = `${pageSize.height}px`;
    image.src = imageUrl;
    image.alt = label;
    image.className = 'flipbook-modal__cover-image';
    image.draggable = false;
    cover.appendChild(image);

    return cover;
  }

  async function renderPdfCover(pdfPageNumber, pageSize, key, label) {
    const page = await pdfDocument.getPage(pdfPageNumber);
    if (key !== renderKey) return null;

    const baseViewport = page.getViewport({ scale: 1 });
    const isMobilePortrait = window.matchMedia('(max-width: 749px)').matches;
    const coverSize = isMobilePortrait
      ? { width: pageSize.width, height: Math.floor(pageSize.width * (baseViewport.height / baseViewport.width)) }
      : pageSize;
    const renderWidth = Math.min(
      MAX_RENDER_WIDTH,
      pageSize.width * 2 * Math.max(3, (window.devicePixelRatio || 1) * 2)
    );
    const renderViewport = page.getViewport({ scale: renderWidth / baseViewport.width });
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d', { alpha: false });

    canvas.width = Math.floor(renderViewport.width);
    canvas.height = Math.floor(renderViewport.height);

    await page.render({ canvasContext: context, viewport: renderViewport }).promise;
    if (key !== renderKey) return null;

    return createCoverPage(canvas.toDataURL('image/png'), label, coverSize, pdfPageNumber === 1 ? 'front' : 'back');
  }

  async function renderPdfSpreadImages(pdfPageNumber, pageSize, key) {
    const page = await pdfDocument.getPage(pdfPageNumber);
    if (key !== renderKey) return [];

    const baseViewport = page.getViewport({ scale: 1 });
    const renderWidth = Math.min(
      MAX_RENDER_WIDTH,
      pageSize.width * (splitSpreads ? 2 : 1) * Math.max(3, (window.devicePixelRatio || 1) * 2)
    );
    const renderViewport = page.getViewport({ scale: renderWidth / baseViewport.width });
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d', { alpha: false });

    canvas.width = Math.floor(renderViewport.width);
    canvas.height = Math.floor(renderViewport.height);

    await page.render({ canvasContext: context, viewport: renderViewport }).promise;
    if (key !== renderKey) return [];

    if (!splitSpreads) {
      return [{
        imageUrl: cropCanvas(canvas, 'full'),
        label: `Page ${pdfPageNumber}`
      }];
    }

    return [
      {
        imageUrl: cropCanvas(canvas, 'left'),
        label: `Page ${(pdfPageNumber * 2) - 1}`
      },
      {
        imageUrl: cropCanvas(canvas, 'right'),
        label: `Page ${pdfPageNumber * 2}`
      }
    ];
  }

  async function renderPdfPageIntoBook(pdfPageNumber, key) {
    if (!activePageSize || renderedPdfPages.has(pdfPageNumber)) return;

    const targetPages = pdfPageGroups.get(pdfPageNumber);
    if (!targetPages?.length) return;

    const renderedPages = await renderPdfSpreadImages(pdfPageNumber, activePageSize, key);
    if (key !== renderKey) return;

    renderedPages.forEach((renderedPage, index) => {
      setFlipPageImage(targetPages[index], renderedPage.imageUrl, renderedPage.label);
    });
    renderedPdfPages.add(pdfPageNumber);
  }

  function getPdfPageForLogicalIndex(logicalIndex) {
    if (coverAsSingle && logicalIndex === 0) return null;
    if (coverAsSingle && logicalIndex === logicalPageCount - 1) return null;

    const bookIndex = logicalIndex - (coverAsSingle ? 1 : 0);
    if (bookIndex < 0) return null;

    return firstBookPdfPage + Math.floor(bookIndex / (splitSpreads ? 2 : 1));
  }

  function getPriorityPdfPages() {
    const pageNumbers = new Set();
    const visiblePages = window.matchMedia('(max-width: 749px)').matches ? 1 : 2;

    for (let index = currentPageIndex - 2; index <= currentPageIndex + visiblePages + 2; index += 1) {
      const pdfPageNumber = getPdfPageForLogicalIndex(index);

      if (pdfPageNumber && pdfPageNumber >= firstBookPdfPage && pdfPageNumber <= lastBookPdfPage) {
        pageNumbers.add(pdfPageNumber);
      }
    }

    return [...pageNumbers];
  }

  function waitForIdle() {
    return new Promise((resolve) => {
      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(resolve, { timeout: 250 });
      } else {
        window.setTimeout(resolve, 40);
      }
    });
  }

  async function renderNearbyPages(key) {
    const pages = getPriorityPdfPages();

    for (const pdfPageNumber of pages) {
      if (key !== renderKey) return;
      await renderPdfPageIntoBook(pdfPageNumber, key);
    }
  }

  async function renderRemainingPages(key) {
    const priorityPages = getPriorityPdfPages();
    const pageNumbers = [];

    for (let pdfPageNumber = firstBookPdfPage; pdfPageNumber <= lastBookPdfPage; pdfPageNumber += 1) {
      pageNumbers.push(pdfPageNumber);
    }

    const queue = [...new Set([...priorityPages, ...pageNumbers])];

    for (const pdfPageNumber of queue) {
      if (key !== renderKey) return;
      await waitForIdle();
      await renderPdfPageIntoBook(pdfPageNumber, key);
    }
  }

  function syncVisibleMode() {
    const activeBook = getActiveBook();
    const showFrontCover = coverAsSingle && currentPageIndex === 0;
    const showBackCover = coverAsSingle && currentPageIndex === logicalPageCount - 1;

    stage.querySelectorAll('[data-flipbook-cover]').forEach((cover) => {
      cover.classList.toggle('is-active', (cover.dataset.flipbookCover === 'front' && showFrontCover) || (cover.dataset.flipbookCover === 'back' && showBackCover));
    });
    activeBook?.classList.toggle('is-hidden-for-cover', showFrontCover || showBackCover);
    updateControls(false);
  }

  function getBookPageIndex(logicalIndex) {
    const maxBookIndex = Math.max(0, ((lastBookPdfPage - firstBookPdfPage + 1) * (splitSpreads ? 2 : 1)) - 1);
    return Math.min(maxBookIndex, Math.max(0, logicalIndex - (coverAsSingle ? 1 : 0)));
  }

  function getPageFlipIndex(instance, fallbackIndex) {
    if (typeof instance?.getCurrentPageIndex === 'function') {
      const currentIndex = instance.getCurrentPageIndex();
      return Number.isFinite(currentIndex) ? currentIndex : fallbackIndex;
    }

    return fallbackIndex;
  }

  async function restorePageFlipPosition(instance, targetPageIndex, key) {
    const bookPageIndex = getBookPageIndex(targetPageIndex);

    for (let attempt = 0; attempt < 3; attempt += 1) {
      if (key !== renderKey) return;

      await new Promise((resolve) => window.requestAnimationFrame(resolve));
      instance.turnToPage(bookPageIndex);
    }

    currentPageIndex = targetPageIndex;
  }

  async function buildFlipbook() {
    if (!pdfDocument) return;

    const key = ++renderKey;
    const PageFlip = await loadPageFlip();
    const spreadLayout = await getSpreadLayout();
    if (!spreadLayout.viewport) return;

    if (pageFlip && !(coverAsSingle && (currentPageIndex === 0 || currentPageIndex === logicalPageCount - 1))) {
      currentPageIndex = getPageFlipIndex(pageFlip, getBookPageIndex(currentPageIndex)) + (coverAsSingle ? 1 : 0);
    }

    splitSpreads = spreadLayout.split;
    coverAsSingle = splitSpreads && pdfDocument.numPages > 1;
    const pageSize = getPageSize(spreadLayout.viewport);
    const pageElements = [];
    const oldPageIndex = Math.min(currentPageIndex, Math.max(0, logicalPageCount - 1));
    firstBookPdfPage = coverAsSingle ? 2 : 1;
    lastBookPdfPage = coverAsSingle ? pdfDocument.numPages - 1 : pdfDocument.numPages;
    activePageSize = pageSize;
    renderedPdfPages = new Set();
    pdfPageGroups = new Map();

    logicalPageCount = (coverAsSingle ? 2 : 0) + ((lastBookPdfPage - firstBookPdfPage + 1) * (splitSpreads ? 2 : 1));
    currentPageIndex = Math.min(oldPageIndex, Math.max(0, logicalPageCount - 1));
    const targetPageIndex = currentPageIndex;

    const currentBook = getActiveBook();
    const buildFrontCover = coverAsSingle ? await renderPdfCover(1, pageSize, key, 'Cover page') : null;
    const buildBackCover = coverAsSingle ? await renderPdfCover(pdfDocument.numPages, pageSize, key, 'Back cover') : null;
    const buildBook = document.createElement('div');
    buildBook.className = 'flipbook-modal__book';
    buildBook.setAttribute('data-flipbook-book', '');
    buildBook.style.width = `${pageSize.width}px`;
    buildBook.style.height = `${pageSize.height}px`;
    setStatus('Preparing magazine...');
    updateControls(true);

    for (let pdfPageNumber = firstBookPdfPage; pdfPageNumber <= lastBookPdfPage; pdfPageNumber += 1) {
      if (key !== renderKey) {
        buildBook.remove();
        return;
      }

      const pages = splitSpreads
        ? [
          createFlipPage('', `Page ${(pdfPageNumber * 2) - 1}`),
          createFlipPage('', `Page ${pdfPageNumber * 2}`)
        ]
        : [createFlipPage('', `Page ${pdfPageNumber}`)];

      pdfPageGroups.set(pdfPageNumber, pages);
      pageElements.push(...pages);
      pages.forEach((page) => buildBook.appendChild(page));
    }

    if (key !== renderKey) {
      buildBook.remove();
      return;
    }

    if (!coverAsSingle || targetPageIndex > 0) {
      await renderNearbyPages(key);
    }

    if (key !== renderKey) {
      buildBook.remove();
      return;
    }

    if (pageFlip) {
      pageFlip.destroy();
    }

    stage.querySelectorAll('[data-flipbook-cover]').forEach((cover) => cover.remove());
    if (currentBook && currentBook.parentNode) {
      currentBook.replaceWith(buildBook);
    } else {
      stage.insertBefore(buildBook, stage.firstChild);
    }
    if (buildFrontCover) stage.insertBefore(buildFrontCover, buildBook);
    if (buildBackCover) stage.appendChild(buildBackCover);
    const isPortraitMode = window.matchMedia('(max-width: 749px)').matches;
    stageBaseWidth = isPortraitMode ? pageSize.width : pageSize.width * 2;
    stageBaseHeight = pageSize.height;
    stage.style.width = `${stageBaseWidth}px`;
    stage.style.height = `${stageBaseHeight}px`;

    const nextPageFlip = new PageFlip(buildBook, {
      width: pageSize.width,
      height: pageSize.height,
      size: 'stretch',
      minWidth: 260,
      maxWidth: pageSize.width,
      minHeight: 180,
      maxHeight: pageSize.height,
      maxShadowOpacity: 0.45,
      showCover: false,
      usePortrait: true,
      useMouseEvents: false,
      mobileScrollSupport: false,
      flippingTime: 900,
      drawShadow: true
    });

    nextPageFlip.loadFromHTML(pageElements);
    nextPageFlip.on('flip', (event) => {
      currentPageIndex = getPageFlipIndex(nextPageFlip, event.data) + (coverAsSingle ? 1 : 0);
      updateControls(false);
      renderNearbyPages(key).catch(() => {});
    });

    if (key !== renderKey) {
      nextPageFlip.destroy();
      return;
    }

    pageFlip = nextPageFlip;
    centerStage();
    setStatus('');

    if (targetPageIndex > 0) {
      await restorePageFlipPosition(nextPageFlip, targetPageIndex, key);
    }

    currentPageIndex = targetPageIndex;
    syncVisibleMode();
    renderRemainingPages(key).catch(() => {});
  }

  async function openModal(url, modalTitle) {
    previousActiveElement = document.activeElement;
    pdfUrl = url;
    pdfDocument = null;
    currentPageIndex = 0;
    logicalPageCount = 0;
    zoomLevel = 1;
    renderKey += 1;

    title.textContent = modalTitle || 'Magazine';
    downloadLink.href = pdfUrl;
    resetBook();
    updateControls(true);

    modal.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('flipbook-modal-open');
    document.body.classList.add('flipbook-modal-open');
    dialog.focus();
    setStatus('Loading magazine...');

    try {
      const pdfjsLib = await loadPdfJs();
      pdfDocument = await pdfjsLib.getDocument({ url: pdfUrl, withCredentials: false }).promise;
      await buildFlipbook();
    } catch (error) {
      console.error('[flipbook] load error:', error);
      setStatus('This PDF could not be loaded as a flipbook. You can still open the PDF directly.');
      resetBook();
    }
  }

  function closeModal() {
    modal.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('flipbook-modal-open');
    document.body.classList.remove('flipbook-modal-open');
    renderKey += 1;
    pdfDocument?.destroy();
    pdfDocument = null;
    logicalPageCount = 0;
    setStatus('');
    resetBook();
    previousActiveElement?.focus?.();
  }

  function nextPage() {
    if (!pageFlip) return;
    if (coverAsSingle && currentPageIndex === 0) {
      currentPageIndex = 1;
      syncVisibleMode();
      pageFlip.turnToPage(0);
      renderNearbyPages(renderKey).catch(() => {});
      return;
    }

    if (coverAsSingle && currentPageIndex >= logicalPageCount - 3) {
      currentPageIndex = logicalPageCount - 1;
      syncVisibleMode();
      return;
    }

    pageFlip.flipNext();
  }

  function prevPage() {
    if (!pageFlip) return;
    if (coverAsSingle && currentPageIndex === logicalPageCount - 1) {
      currentPageIndex = logicalPageCount - 3;
      syncVisibleMode();
      pageFlip.turnToPage(getBookPageIndex(currentPageIndex));
      renderNearbyPages(renderKey).catch(() => {});
      return;
    }

    if (coverAsSingle && currentPageIndex === 1) {
      currentPageIndex = 0;
      syncVisibleMode();
      return;
    }

    pageFlip.flipPrev();
  }

  function setZoom(nextZoom) {
    if (!pdfDocument) return;

    zoomLevel = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, nextZoom));
    setStatus('');
    centerStage();
    updateControls(false);
  }

  function startPan(event) {
    if (event.button !== undefined && event.button !== 0) return;

    const { maxX, maxY } = getPanBounds();
    if (maxX <= 0 && maxY <= 0) return;

    isDragging = true;
    dragStartX = event.clientX;
    dragStartY = event.clientY;
    dragOriginX = panX;
    dragOriginY = panY;
    viewport.classList.add('is-dragging');
    viewport.setPointerCapture?.(event.pointerId);
  }

  function movePan(event) {
    if (!isDragging) return;

    event.preventDefault();
    panX = dragOriginX + event.clientX - dragStartX;
    panY = dragOriginY + event.clientY - dragStartY;
    applyPan();
  }

  function endPan(event) {
    if (!isDragging) return;

    isDragging = false;
    viewport.classList.remove('is-dragging');
    viewport.releasePointerCapture?.(event.pointerId);
  }

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-flipbook-trigger]');
    if (!trigger) return;

    const href = trigger.getAttribute('href');
    if (!href || !href.toLowerCase().includes('.pdf')) return;

    event.preventDefault();
    openModal(href, trigger.dataset.flipbookTitle || trigger.textContent.trim());
  });

  modal.addEventListener('click', (event) => {
    if (event.target.closest('[data-flipbook-close]')) closeModal();
  });

  prevButtons.forEach((button) => {
    button.addEventListener('click', prevPage);
  });
  nextButtons.forEach((button) => {
    button.addEventListener('click', nextPage);
  });
  zoomOutButton.addEventListener('click', () => setZoom(zoomLevel - ZOOM_STEP));
  zoomInButton.addEventListener('click', () => setZoom(zoomLevel + ZOOM_STEP));
  viewport.addEventListener('pointerdown', startPan);
  viewport.addEventListener('pointermove', movePan);
  viewport.addEventListener('pointerup', endPan);
  viewport.addEventListener('pointercancel', endPan);

  document.addEventListener('keydown', (event) => {
    if (modal.getAttribute('aria-hidden') === 'true') return;
    if (event.key === 'Escape') closeModal();
    if (event.key === 'ArrowLeft') prevPage();
    if (event.key === 'ArrowRight') nextPage();
    if ((event.key === '+' || event.key === '=') && (event.ctrlKey || event.metaKey)) {
      event.preventDefault();
      setZoom(zoomLevel + ZOOM_STEP);
    }
    if (event.key === '-' && (event.ctrlKey || event.metaKey)) {
      event.preventDefault();
      setZoom(zoomLevel - ZOOM_STEP);
    }
  });

  window.addEventListener('resize', () => {
    if (modal.getAttribute('aria-hidden') === 'true' || !pdfDocument) return;

    window.clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(() => {
      buildFlipbook().catch(() => {
        setStatus('');
        centerStage();
        updateControls(false);
      });
    }, 250);
  });

  window.FLCFlipbookModal = { open: openModal, close: closeModal };
})();
